import React, { useRef, useEffect, useState } from 'react';
import './Billiards.css';

// TABLE AND BALL SETTINGS
const TABLE_HEIGHT = window.innerHeight - 160;
const TABLE_WIDTH = TABLE_HEIGHT / 2;
const BALL_RADIUS = TABLE_WIDTH / 44;  // ball diameter = TABLE_WIDTH/22
const POCKET_RADIUS = BALL_RADIUS * 2.28; // pocket diameter = 2.28 * ball diameter
const MAX_SHOT_POWER = 25;     // initial cue ball shot power
const MAX_COLLISION_POWER = 11;
const FRICTION = 0.985;
const COLLISION_RESTITUTION = 0.95;

// UTILITY: distance between points
function dist(x1, y1, x2, y2) {
  return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
}

// CREATE BALLS – each non–cue ball gets an extra property "recorded"
function createBalls() {
  const balls = [];
  
  // Cue ball (do not record)
  balls.push({
    id: 'cue',
    x: TABLE_WIDTH * 0.25,
    y: TABLE_HEIGHT * 0.2,
    vx: 0,
    vy: 0,
    color: 'white',
    number: '',
    isCue: true,
    pocketed: false
  });
  
  // Rack of 15 balls arranged in a triangle.
  const startX = TABLE_WIDTH * 0.5;
  const startY = TABLE_HEIGHT * 0.75 + BALL_RADIUS * 3;
  const spacing = BALL_RADIUS * 2.1;
  const colors = ['yellow', 'blue', 'red', 'purple', 'orange', 'green', 'maroon'];
  const rackPattern = [
    [11, 12, 13, 14, 15],
    [6, 7, 9, 10],
    [4, 8, 5],
    [2, 3],
    [1]
  ];
  
  for (let row = 0; row < rackPattern.length; row++) {
    const rowBalls = rackPattern[row];
    for (let col = 0; col < rowBalls.length; col++) {
      const number = rowBalls[col];
      const x = startX + (col - (rowBalls.length - 1) / 2) * spacing * 0.9;
      const y = startY - row * spacing * 0.9;
      if (number === 8) {
        balls.push({
          id: '8-ball',
          x, y, vx: 0, vy: 0,
          color: 'black',
          number: '8',
          isCue: false,
          pocketed: false,
          recorded: false
        });
      } else {
        const isStripe = number > 8;
        const colorIndex = ((number - 1) % 7);
        balls.push({
          id: `ball${number}`,
          x, y, vx: 0, vy: 0,
          color: colors[colorIndex],
          number: number.toString(),
          isStripe,
          isCue: false,
          pocketed: false,
          recorded: false
        });
      }
    }
  }
  
  return balls;
}

/**
 * Simulate a shot offline to generate predicted paths.
 * "reverse" determines if the y–component is inverted (true for traditional aim).
 */
function simulateShot(angle, power, reverse = true, ballsState) {
  const simBalls = ballsState.map(b => ({ ...b }));
  const cue = simBalls.find(b => b.isCue && !b.pocketed);
  if (!cue) return null;
  cue.vx = Math.cos(angle) * power;
  cue.vy = reverse ? -Math.sin(angle) * power : Math.sin(angle) * power;
  
  const paths = {};
  simBalls.forEach(b => {
    paths[b.id] = [{ x: b.x, y: b.y }];
  });
  
  const maxSteps = 300;
  const pockets = [
    { x: 0, y: 0 },
    { x: TABLE_WIDTH, y: 0 },
    { x: 0, y: TABLE_HEIGHT / 2 },
    { x: TABLE_WIDTH, y: TABLE_HEIGHT / 2 },
    { x: 0, y: TABLE_HEIGHT },
    { x: TABLE_WIDTH, y: TABLE_HEIGHT }
  ];
  
  for (let step = 0; step < maxSteps; step++) {
    simBalls.forEach(b => {
      if (b.pocketed) return;
      b.x += b.vx;
      b.y += b.vy;
      b.vx *= FRICTION;
      b.vy *= FRICTION;
      if (b.x < BALL_RADIUS) {
        b.x = BALL_RADIUS;
        b.vx = -b.vx * COLLISION_RESTITUTION;
      } else if (b.x > TABLE_WIDTH - BALL_RADIUS) {
        b.x = TABLE_WIDTH - BALL_RADIUS;
        b.vx = -b.vx * COLLISION_RESTITUTION;
      }
      if (b.y < BALL_RADIUS) {
        b.y = BALL_RADIUS;
        b.vy = -b.vy * COLLISION_RESTITUTION;
      } else if (b.y > TABLE_HEIGHT - BALL_RADIUS) {
        b.y = TABLE_HEIGHT - BALL_RADIUS;
        b.vy = -b.vy * COLLISION_RESTITUTION;
      }
    });
    
    // Ball-to-ball collisions
    for (let i = 0; i < simBalls.length; i++) {
      for (let j = i + 1; j < simBalls.length; j++) {
        const b1 = simBalls[i];
        const b2 = simBalls[j];
        if (b1.pocketed || b2.pocketed) continue;
        const dx = b2.x - b1.x;
        const dy = b2.y - b1.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < BALL_RADIUS * 2) {
          const nx = dx / distance;
          const ny = dy / distance;
          const dvx = b2.vx - b1.vx;
          const dvy = b2.vy - b1.vy;
          const normalVelocity = dvx * nx + dvy * ny;
          if (normalVelocity < 0) {
            const impulse = -(1 + COLLISION_RESTITUTION) * normalVelocity * (MAX_COLLISION_POWER / MAX_SHOT_POWER);
            b1.vx -= impulse * nx;
            b1.vy -= impulse * ny;
            b2.vx += impulse * nx;
            b2.vy += impulse * ny;
            const overlap = (BALL_RADIUS * 2 - distance) / 2;
            b1.x -= overlap * nx;
            b1.y -= overlap * ny;
            b2.x += overlap * nx;
            b2.y += overlap * ny;
          }
        }
      }
    }
    
    // Check for pockets
    simBalls.forEach(b => {
      if (b.pocketed) return;
      for (let pk of pockets) {
        if (dist(b.x, b.y, pk.x, pk.y) < POCKET_RADIUS) {
          b.pocketed = true;
          b.vx = 0;
          b.vy = 0;
          break;
        }
      }
    });
    
    simBalls.forEach(b => {
      if (!b.pocketed) {
        paths[b.id].push({ x: b.x, y: b.y });
      }
    });
    
    const allStopped = simBalls.every(b => b.pocketed || (Math.abs(b.vx) < 0.01 && Math.abs(b.vy) < 0.01));
    if (allStopped) break;
  }
  return paths;
}

export default function Billiards() {
  // === GAME STATE VARIABLES ===
  // (If gameMode is null, we show the mode selection screen.)
  const [gameMode, setGameMode] = useState(null); // "freeplay", "single", "twoplayer"
  const [currentPlayer, setCurrentPlayer] = useState("player1"); // "player1" or "player2"
  const [player1Group, setPlayer1Group] = useState(null); // "solids" or "stripes"
  const [player2Group, setPlayer2Group] = useState(null);
  const [pocketedPlayer1, setPocketedPlayer1] = useState([]);
  const [pocketedPlayer2, setPocketedPlayer2] = useState([]);
  const [winner, setWinner] = useState(null);

  // Existing state for aiming/physics
  const [balls, setBalls] = useState(createBalls());
  const requestRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchPos, setTouchPos] = useState(null);
  const [aimAngle, setAimAngle] = useState(0);
  const [powerLevel, setPowerLevel] = useState(0);
  const [predictedPaths, setPredictedPaths] = useState(null);
  // Retain aim mode dropdown support (Traditional vs. Precision)
  const [aimMode, setAimMode] = useState("traditional");
  const [precisionAim, setPrecisionAim] = useState(null);
  const [precisionPower, setPrecisionPower] = useState(null);
  const [shotInProgress, setShotInProgress] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const powerMeterRef = useRef(null);

  // === GAME LOOP (physics) ===
  function gameLoop() {
    setBalls(prev => {
      const updated = prev.map(b => ({ ...b }));
      
      // Update ball positions and apply friction
      updated.forEach(b => {
        if (b.pocketed) return;
        b.x += b.vx;
        b.y += b.vy;
        b.vx *= FRICTION;
        b.vy *= FRICTION;
        if (Math.abs(b.vx) < 0.01) b.vx = 0;
        if (Math.abs(b.vy) < 0.01) b.vy = 0;
        if (b.x < BALL_RADIUS) {
          b.x = BALL_RADIUS;
          b.vx = -b.vx * COLLISION_RESTITUTION;
        } else if (b.x > TABLE_WIDTH - BALL_RADIUS) {
          b.x = TABLE_WIDTH - BALL_RADIUS;
          b.vx = -b.vx * COLLISION_RESTITUTION;
        }
        if (b.y < BALL_RADIUS) {
          b.y = BALL_RADIUS;
          b.vy = -b.vy * COLLISION_RESTITUTION;
        } else if (b.y > TABLE_HEIGHT - BALL_RADIUS) {
          b.y = TABLE_HEIGHT - BALL_RADIUS;
          b.vy = -b.vy * COLLISION_RESTITUTION;
        }
      });
      
      // Ball-to-ball collisions
      for (let i = 0; i < updated.length; i++) {
        for (let j = i + 1; j < updated.length; j++) {
          const b1 = updated[i];
          const b2 = updated[j];
          if (b1.pocketed || b2.pocketed) continue;
          const dx = b2.x - b1.x;
          const dy = b2.y - b1.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < BALL_RADIUS * 2) {
            const nx = dx / distance;
            const ny = dy / distance;
            const dvx = b2.vx - b1.vx;
            const dvy = b2.vy - b1.vy;
            const normalVelocity = dvx * nx + dvy * ny;
            if (normalVelocity < 0) {
              const impulse = -(1 + COLLISION_RESTITUTION) * normalVelocity * (MAX_COLLISION_POWER / MAX_SHOT_POWER);
              b1.vx -= impulse * nx;
              b1.vy -= impulse * ny;
              b2.vx += impulse * nx;
              b2.vy += impulse * ny;
              const overlap = (BALL_RADIUS * 2 - distance) / 2;
              b1.x -= overlap * nx;
              b1.y -= overlap * ny;
              b2.x += overlap * nx;
              b2.y += overlap * ny;
            }
          }
        }
      }
      
      // Pocket detection
      const pockets = [
        { x: 0, y: 0 },
        { x: TABLE_WIDTH, y: 0 },
        { x: 0, y: TABLE_HEIGHT / 2 },
        { x: TABLE_WIDTH, y: TABLE_HEIGHT / 2 },
        { x: 0, y: TABLE_HEIGHT },
        { x: TABLE_WIDTH, y: TABLE_HEIGHT }
      ];
      updated.forEach(b => {
        if (b.pocketed) return;
        for (let pk of pockets) {
          if (dist(b.x, b.y, pk.x, pk.y) < POCKET_RADIUS) {
            b.pocketed = true;
            b.vx = 0;
            b.vy = 0;
            break;
          }
        }
      });
      return updated;
    });
    requestRef.current = requestAnimationFrame(gameLoop);
  }
  
  useEffect(() => {
    requestRef.current = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(requestRef.current);
  }, []);
  
  // === SHOT COMPLETION & TURN SWITCHING ===
  useEffect(() => {
    if (shotInProgress) {
      const allStopped = balls.every(b => Math.abs(b.vx) < 0.01 && Math.abs(b.vy) < 0.01);
      if (allStopped) {
        endShot();
      }
    }
  }, [balls, shotInProgress]);
  
  function endShot() {
    // Process newly pocketed balls (that have not been recorded)
    setBalls(prev =>
      prev.map(b => {
        if (!b.recorded && b.pocketed && !b.isCue) {
          b.recorded = true;
          if (b.number !== "8") {
            const ballGroup = b.isStripe ? "stripes" : "solids";
            if (currentPlayer === "player1") {
              if (!player1Group) {
                setPlayer1Group(ballGroup);
                setPlayer2Group(ballGroup === "solids" ? "stripes" : "solids");
              }
              setPocketedPlayer1(prevArr => [...prevArr, b]);
            } else {
              if (!player2Group) {
                setPlayer2Group(ballGroup);
                setPlayer1Group(ballGroup === "solids" ? "stripes" : "solids");
              }
              setPocketedPlayer2(prevArr => [...prevArr, b]);
            }
          }
        }
        return b;
      })
    );
    
    const eightBall = balls.find(b => b.id === "8-ball");
    if (eightBall && eightBall.recorded) {
      const requiredCount = 7;
      if (currentPlayer === "player1") {
        if (pocketedPlayer1.length >= requiredCount) setWinner("player1");
        else setWinner("player2");
      } else {
        if (pocketedPlayer2.length >= requiredCount) setWinner("player2");
        else setWinner("player1");
      }
    }
    
    if (gameMode !== "freeplay" && !winner) {
      setCurrentPlayer(prev => (prev === "player1" ? "player2" : "player1"));
    }
    setShotInProgress(false);
  }
  
  // === COMPUTER SHOT (for single–player mode) ===
  useEffect(() => {
    if (gameMode === "single" && currentPlayer === "player2" && !shotInProgress && !winner) {
      const timeout = setTimeout(() => {
        computerShot();
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [gameMode, currentPlayer, shotInProgress, winner]);
  
  function computerShot() {
    console.log("Computer shot function called");
    // Choose a target ball: if computer's group is set, pick the first available ball of that group.
    let target;
    if (player2Group) {
      target = balls.find(b =>
        !b.pocketed && !b.isCue && b.number !== "8" &&
        ((player2Group === "solids" && !b.isStripe) ||
         (player2Group === "stripes" && b.isStripe))
      );
    }
    if (!target) {
      target = balls.find(b => !b.pocketed && !b.isCue && b.number !== "8");
    }
    if (!target) {
      target = { x: TABLE_WIDTH / 2, y: TABLE_HEIGHT / 2 };
    }
    
    const pockets = [
      { x: 0, y: 0 },
      { x: TABLE_WIDTH, y: 0 },
      { x: 0, y: TABLE_HEIGHT / 2 },
      { x: TABLE_WIDTH, y: TABLE_HEIGHT / 2 },
      { x: 0, y: TABLE_HEIGHT },
      { x: TABLE_WIDTH, y: TABLE_HEIGHT }
    ];
    let chosenPocket = pockets[0];
    let minDist = dist(target.x, target.y, pockets[0].x, pockets[0].y);
    for (let pk of pockets) {
      const d = dist(target.x, target.y, pk.x, pk.y);
      if (d < minDist) {
        minDist = d;
        chosenPocket = pk;
      }
    }
    
    const desiredDir = Math.atan2(chosenPocket.y - target.y, chosenPocket.x - target.x);
    const contactPoint = {
      x: target.x - BALL_RADIUS * Math.cos(desiredDir),
      y: target.y - BALL_RADIUS * Math.sin(desiredDir)
    };
    
    const cue = balls.find(b => b.isCue && !b.pocketed);
    if (!cue) return;
    const shotAngle = Math.atan2(contactPoint.y - cue.y, contactPoint.x - cue.x);
    const power = 20; // fixed power for computer shot
    console.log("Computer shot:", { shotAngle, power });
    setBalls(prev => prev.map(b => {
      if (!b.isCue) return b;
      return { ...b, vx: Math.cos(shotAngle) * power, vy: -Math.sin(shotAngle) * power };
    }));
    setShotInProgress(true);
  }
  
  // === TOUCH HANDLERS (Disable input when it's computer's turn) ===
  function handleTouchStart(e) {
    e.preventDefault();
    if (gameMode === "single" && currentPlayer === "player2") return;
    setShowInstructions(false);
    const rect = e.currentTarget.getBoundingClientRect();
    if (aimMode === "traditional") {
      if (!e.touches || e.touches.length < 1) return;
      const tx = e.touches[0].clientX - rect.left;
      const ty = e.touches[0].clientY - rect.top;
      const cue = balls.find(b => b.isCue && !b.pocketed);
      if (!cue) return;
      const d = dist(tx, ty, cue.x, TABLE_HEIGHT - cue.y);
      if (d < BALL_RADIUS * 4) {
        setIsDragging(true);
        setDragStart({ x: cue.x, y: cue.y });
        setTouchPos({ x: tx, y: ty });
        if (powerMeterRef.current) {
          powerMeterRef.current.style.display = 'block';
        }
        const dx = tx - cue.x;
        const dy = ty - (TABLE_HEIGHT - cue.y);
        const angle = Math.atan2(dy, dx);
        setAimAngle(angle + Math.PI);
        const dragDist = Math.sqrt(dx * dx + dy * dy);
        const maxDist = TABLE_WIDTH * 0.3;
        const power = Math.min(100, (dragDist / maxDist) * 100);
        setPowerLevel(power);
      }
    } else {
      for (let i = 0; i < e.changedTouches.length; i++) {
        let touch = e.changedTouches[i];
        const tx = touch.clientX - rect.left;
        const ty = touch.clientY - rect.top;
        if (!precisionAim) {
          setPrecisionAim({ id: touch.identifier, x: tx, y: ty });
        } else if (!precisionPower) {
          const cue = balls.find(b => b.isCue && !b.pocketed);
          if (!cue) continue;
          const d = dist(tx, ty, cue.x, TABLE_HEIGHT - cue.y);
          if (d < BALL_RADIUS * 3) {
            setPrecisionPower({ id: touch.identifier, x: tx, y: ty });
            if (powerMeterRef.current) {
              powerMeterRef.current.style.display = 'block';
            }
          }
        }
      }
    }
  }
  
  function handleTouchMove(e) {
    e.preventDefault();
    if (gameMode === "single" && currentPlayer === "player2") return;
    const rect = e.currentTarget.getBoundingClientRect();
    const cue = balls.find(b => b.isCue && !b.pocketed);
    if (!cue) return;
    if (aimMode === "traditional") {
      if (!isDragging) return;
      const tx = e.touches[0].clientX - rect.left;
      const ty = e.touches[0].clientY - rect.top;
      setTouchPos({ x: tx, y: ty });
      const dx = tx - cue.x;
      const dy = ty - (TABLE_HEIGHT - cue.y);
      const angle = Math.atan2(dy, dx);
      setAimAngle(angle + Math.PI);
      const dragDist = Math.sqrt(dx * dx + dy * dy);
      const maxDist = TABLE_WIDTH * 0.3;
      const power = Math.min(100, (dragDist / maxDist) * 100);
      setPowerLevel(power);
      const shotPower = (power * MAX_SHOT_POWER) / 100;
      const newPaths = simulateShot(angle + Math.PI, shotPower, true, balls);
      setPredictedPaths(newPaths);
    } else {
      let newAim = precisionAim;
      let newPower = precisionPower;
      for (let i = 0; i < e.touches.length; i++) {
        let touch = e.touches[i];
        const tx = touch.clientX - rect.left;
        const ty = touch.clientY - rect.top;
        if (precisionAim && touch.identifier === precisionAim.id) {
          newAim = { id: precisionAim.id, x: tx, y: ty };
        }
        if (precisionPower && touch.identifier === precisionPower.id) {
          newPower = { id: precisionPower.id, x: tx, y: ty };
        }
      }
      setPrecisionAim(newAim);
      setPrecisionPower(newPower);
      const simAim = { x: newAim.x, y: TABLE_HEIGHT - newAim.y };
      const vecX = simAim.x - cue.x;
      const vecY = simAim.y - cue.y;
      const angle = Math.atan2(vecY, vecX);
      setAimAngle(angle);
      if (newPower) {
        const simPower = { x: newPower.x, y: TABLE_HEIGHT - newPower.y };
        const powerDx = simPower.x - cue.x;
        const powerDy = simPower.y - cue.y;
        const dragDist = Math.sqrt(powerDx * powerDx + powerDy * powerDy);
        const maxDist = TABLE_WIDTH * 0.3;
        const power = Math.min(100, (dragDist / maxDist) * 100);
        setPowerLevel(power);
        const shotPower = (power * MAX_SHOT_POWER) / 100;
        const newPaths = simulateShot(angle, shotPower, false, balls);
        setPredictedPaths(newPaths);
      }
    }
  }
  
  function handleTouchEnd(e) {
    e.preventDefault();
    if (gameMode === "single" && currentPlayer === "player2") return;
    const cue = balls.find(b => b.isCue && !b.pocketed);
    if (!cue) return;
    if (aimMode === "traditional") {
      if (!isDragging) return;
      const power = (powerLevel * MAX_SHOT_POWER) / 100;
      const vx = Math.cos(aimAngle) * power;
      const vy = -Math.sin(aimAngle) * power;
      setBalls(prev => prev.map(b => {
        if (!b.isCue) return b;
        return { ...b, vx, vy };
      }));
      setShotInProgress(true);
      setIsDragging(false);
      setTouchPos(null);
      setPowerLevel(0);
      if (powerMeterRef.current) powerMeterRef.current.style.display = 'none';
      setPredictedPaths(null);
    } else {
      let endedPower = false;
      for (let i = 0; i < e.changedTouches.length; i++) {
        let touch = e.changedTouches[i];
        if (precisionPower && touch.identifier === precisionPower.id) {
          endedPower = true;
        }
        if (precisionAim && touch.identifier === precisionAim.id) {
          setPrecisionAim(null);
          setPrecisionPower(null);
          setPredictedPaths(null);
          setPowerLevel(0);
          if (powerMeterRef.current) powerMeterRef.current.style.display = 'none';
          return;
        }
      }
      if (endedPower) {
        const power = (powerLevel * MAX_SHOT_POWER) / 100;
        const vx = Math.cos(aimAngle) * power;
        const vy = Math.sin(aimAngle) * power;
        setBalls(prev => prev.map(b => {
          if (!b.isCue) return b;
          return { ...b, vx, vy };
        }));
        setShotInProgress(true);
        setPrecisionAim(null);
        setPrecisionPower(null);
        setPredictedPaths(null);
        setPowerLevel(0);
        if (powerMeterRef.current) powerMeterRef.current.style.display = 'none';
      }
    }
  }
  
  // === RENDERING ===
  if (gameMode === null) {
    return (
      <div className="game-mode-selection">
        <h2>Select Game Mode</h2>
        <button onClick={() => { setGameMode("freeplay"); setShowInstructions(true); }}>Free Play</button>
        <button onClick={() => { setGameMode("single"); setCurrentPlayer("player1"); setShowInstructions(true); }}>Single Player</button>
        <button onClick={() => { setGameMode("twoplayer"); setCurrentPlayer("player1"); setShowInstructions(true); }}>Two Player</button>
      </div>
    );
  }
  
  return (
    <div className="billiards-container">
      {/* Display turn indicator */}
      {gameMode !== "freeplay" && !winner && (
        <div className="turn-indicator">
          {gameMode === "single"
            ? (currentPlayer === "player1" ? "Your Turn" : "Computer's Turn")
            : (currentPlayer === "player1" ? "Player 1's Turn" : "Player 2's Turn")}
          {player1Group && player2Group && (
            <span className="turn-group-info">
              {currentPlayer === "player1" ? `(Your group: ${player1Group})` : `(Their group: ${player2Group})`}
            </span>
          )}
        </div>
      )}
      
      {/* AIM MODE DROPDOWN */}
      <div className="aim-mode-selector" style={{ marginBottom: '10px', color: 'white' }}>
        <label style={{ marginRight: '5px' }}>Aim Mode:</label>
        <select 
          value={aimMode} 
          onChange={(e) => {
            setAimMode(e.target.value);
            setIsDragging(false);
            setPrecisionAim(null);
            setPrecisionPower(null);
            setPredictedPaths(null);
            setPowerLevel(0);
            setShowInstructions(true);
          }}
        >
          <option value="traditional">Traditional Aim</option>
          <option value="precision">Precision Aim</option>
        </select>
      </div>
      
      {/* The billiards table */}
      <div 
        className="billiards-table"
        style={{ width: TABLE_WIDTH, height: TABLE_HEIGHT }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {balls.map(b => !b.pocketed && (
          <div
            key={b.id}
            className="billiard-ball"
            style={{
              left: b.x,
              top: TABLE_HEIGHT - b.y,
              backgroundColor: b.color,
              width: BALL_RADIUS * 2,
              height: BALL_RADIUS * 2,
              backgroundImage: b.isStripe 
                ? `linear-gradient(white 25%, ${b.color} 25% 75%, white 75%)`
                : 'none'
            }}
          >
            {b.number}
          </div>
        ))}
        
        {[
          { x: 0, y: 0 },
          { x: TABLE_WIDTH, y: 0 },
          { x: 0, y: TABLE_HEIGHT / 2 },
          { x: TABLE_WIDTH, y: TABLE_HEIGHT / 2 },
          { x: 0, y: TABLE_HEIGHT },
          { x: TABLE_WIDTH, y: TABLE_HEIGHT }
        ].map((pocket, index) => (
          <div
            key={`pocket-${index}`}
            className="pocket"
            style={{
              left: pocket.x,
              top: pocket.y,
              width: POCKET_RADIUS * 2,
              height: POCKET_RADIUS * 2,
            }}
          />
        ))}
        
        {((aimMode === "traditional" && isDragging) || (aimMode === "precision" && precisionAim)) && predictedPaths && (
          <svg
            className="prediction-layer"
            width={TABLE_WIDTH}
            height={TABLE_HEIGHT}
            style={{ position: 'absolute', left: 0, top: 0 }}
          >
            {Object.entries(predictedPaths).map(([ballId, positions]) => {
              const ball = balls.find(b => b.id === ballId);
              if (!ball) return null;
              const points = positions.map(pos => `${pos.x},${TABLE_HEIGHT - pos.y}`).join(' ');
              const strokeColor = ball.color || 'white';
              return (
                <polyline
                  key={ballId}
                  points={points}
                  fill="none"
                  stroke={strokeColor}
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  opacity="0.7"
                />
              );
            })}
          </svg>
        )}
        
        {aimMode === "traditional" && isDragging && touchPos && (
          <>
            <div 
              className="aim-line"
              style={{
                left: balls.find(b => b.isCue)?.x,
                top: TABLE_HEIGHT - balls.find(b => b.isCue)?.y,
                width: TABLE_WIDTH * 0.3,
                transform: `rotate(${aimAngle}rad)`
              }}
            />
            <div 
              className="touch-indicator"
              style={{
                left: touchPos.x,
                top: touchPos.y,
                display: 'block'
              }}
            />
            <div className="power-meter" ref={powerMeterRef}>
              <div className="power-level" style={{ width: `${powerLevel}%` }} />
            </div>
          </>
        )}
        
        {aimMode === "precision" && precisionAim && (
          <>
            <div 
              className="aim-line"
              style={{
                left: balls.find(b => b.isCue)?.x,
                top: TABLE_HEIGHT - balls.find(b => b.isCue)?.y,
                width: TABLE_WIDTH * 0.3,
                transform: `rotate(${aimAngle}rad)`
              }}
            />
            <div 
              className="touch-indicator"
              style={{
                left: precisionAim.x,
                top: precisionAim.y,
                display: 'block'
              }}
            />
            {precisionPower && (
              <div className="power-meter" ref={powerMeterRef}>
                <div className="power-level" style={{ width: `${powerLevel}%` }} />
              </div>
            )}
          </>
        )}
        
        {showInstructions && (
          <div className="instructions visible">
            {aimMode === "traditional" ? (
              <>
                <p>Touch and drag from the cue ball to aim and set power</p>
                <p>Release to shoot</p>
              </>
            ) : (
              <>
                <p>Touch anywhere to set aim</p>
                <p>Then touch-drag on the cue ball to set power and release to shoot</p>
              </>
            )}
          </div>
        )}
      </div>
      
      {gameMode !== "freeplay" && (
        <div className="pocketed-display">
          <div className="player-display">
            <h3>{gameMode === "single" ? "Player" : "Player 1"}</h3>
            <div>
              {pocketedPlayer1.map(ball => (
                <div 
                  key={ball.id} 
                  className="pocketed-ball"
                  style={{ backgroundColor: ball.color }}
                >
                  {ball.number}
                </div>
              ))}
            </div>
          </div>
          <div className="player-display">
            <h3>{gameMode === "single" ? "Computer" : "Player 2"}</h3>
            <div>
              {pocketedPlayer2.map(ball => (
                <div 
                  key={ball.id} 
                  className="pocketed-ball"
                  style={{ backgroundColor: ball.color }}
                >
                  {ball.number}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {winner && (
        <div className="win-message">
          {gameMode === "single"
            ? (winner === "player1" ? "You Win!" : "Computer Wins!")
            : (winner === "player1" ? "Player 1 Wins!" : "Player 2 Wins!")}
        </div>
      )}
    </div>
  );
}
